import { useCallback, useEffect, useState } from "react";

/**
 * Shared PWA install controller.
 *
 * - Captures the `beforeinstallprompt` event so callers can trigger the
 *   native install flow on demand (used by the sidebar "Instalar app" item).
 * - Detects iOS Safari (no beforeinstallprompt) so callers can render an
 *   Add-to-Home-Screen instruction instead.
 * - Hides itself once the app is running standalone (already installed).
 * - Dismissal is soft — since the sidebar item is a passive suggestion we
 *   only hide it after a successful install; there is no per-session nag.
 */
export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (isStandalone()) { setInstalled(true); return; }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    if (isIOS()) setIos(true);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return "unavailable";
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    return choice?.outcome || "dismissed";
  }, [deferredPrompt]);

  // canInstall = we have a deferredPrompt (Android/Desktop Chromium) OR the
  // user is on iOS where we can only show instructions.
  const canInstall = !installed && (Boolean(deferredPrompt) || ios);
  const platform = deferredPrompt ? "native" : (ios ? "ios" : "unknown");

  return { canInstall, install, installed, platform };
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}
